import { dev } from '$app/environment';
import { PUBLIC_APP_ENV, PUBLIC_DATADOG_CLIENT_TOKEN } from '$env/static/public';
import { datadogLogs, type LoggerConfiguration } from '@datadog/browser-logs';

type LogLevel = LoggerConfiguration['level'];

interface LoggerOptions {
	minLevel?: LogLevel;
	anonymousId?: string;
}

class Logger {
	#currentScope = '';
	#minLevel: LogLevel = 'info';
	#isDatadogInitialized = false;

	anonymousId = '';
	static #instance: Logger | null = null;

	static #isDev = dev || PUBLIC_APP_ENV === 'test';

	static #getLevelPriority(level: LogLevel): number {
		switch (level) {
			case 'debug':
				return 0;
			case 'info':
				return 1;
			case 'warn':
				return 2;
			case 'error':
				return 3;
			default:
				return 0;
		}
	}

	#shouldProcessLevel(level: LogLevel): boolean {
		return Logger.#getLevelPriority(level) >= Logger.#getLevelPriority(this.#minLevel);
	}

	#initializeDatadog(): void {
		if (this.#isDatadogInitialized || Logger.#isDev || !PUBLIC_DATADOG_CLIENT_TOKEN) {
			return;
		}

		try {
			datadogLogs.init({
				clientToken: PUBLIC_DATADOG_CLIENT_TOKEN,
				site: 'us3.datadoghq.com',
				forwardErrorsToLogs: true,
				sessionSampleRate: 100
			});
			this.#isDatadogInitialized = true;
		} catch (error) {
			console.error('Failed to initialize Datadog:', error);
		}
	}

	private constructor(options?: LoggerOptions) {
		if (options?.minLevel) {
			this.#minLevel = options.minLevel;
		}

		if (options?.anonymousId) {
			this.anonymousId = options.anonymousId;
		}
	}

	static getInstance(options?: LoggerOptions): Logger {
		if (!Logger.#instance) {
			Logger.#instance = new Logger(options);
		} else if (options) {
			if (options.minLevel) {
				Logger.#instance.#minLevel = options.minLevel;
			}
			if (options.anonymousId) {
				Logger.#instance.anonymousId = options.anonymousId;
			}
		}

		return Logger.#instance;
	}

	#devLog = (
		msg: string,
		level: LogLevel = 'info',
		extra: Record<string, unknown> = {},
		stack?: string
	): void => {
		if (!this.#shouldProcessLevel(level)) {
			return;
		}

		const text = this.#currentScope.length > 0 ? `${this.#currentScope}: ${msg}` : msg;

		switch (level) {
			case 'warn':
				console.warn(text);
				break;
			case 'error':
				console.error(text);
				break;
			case 'debug':
			case 'info':
			default:
				console.log(text);
		}

		if (Object.keys(extra).length > 0) {
			console.table(extra);
		}

		if (stack) {
			console.error('Stack trace:', stack);
		} else if (level === 'error' || level === 'warn') {
			console.trace();
		}
	};

	#log = (
		msg: string,
		level: LogLevel = 'info',
		extra: Record<string, unknown> = {},
		stack?: string
	): void => {
		if (!this.#shouldProcessLevel(level)) {
			return;
		}

		if (Logger.#isDev) {
			this.#devLog(msg, level, extra, stack);
			return;
		}

		this.#initializeDatadog();

		try {
			datadogLogs.logger[level](msg, {
				anonymousId: this.anonymousId,
				scope: this.#currentScope,
				...extra,
				stack
			});
		} catch (error) {
			console.error('Error logging to Datadog:', error);
		}
	};

	scope(scopeName: string): Logger {
		const scopedLogger = new Logger();
		scopedLogger.#currentScope = scopeName;
		scopedLogger.anonymousId = this.anonymousId;
		scopedLogger.#minLevel = this.#minLevel;
		scopedLogger.#isDatadogInitialized = this.#isDatadogInitialized;
		return scopedLogger;
	}

	setMinLevel(level: LogLevel): void {
		this.#minLevel = level;
	}

	setAnonymousId(id: string): void {
		this.anonymousId = id;
	}

	debug = (msg: string, extra: Record<string, unknown> = {}): void =>
		this.#log(msg, 'debug', extra);

	info = (msg: string, extra: Record<string, unknown> = {}): void => this.#log(msg, 'info', extra);

	warn = (msg: string, extra: Record<string, unknown> = {}): void => this.#log(msg, 'warn', extra);

	error = (msg: string, extra: Record<string, unknown> = {}): void =>
		this.#log(msg, 'error', extra);

	exception = (error: unknown, extra: Record<string, unknown> = {}): void => {
		if (error instanceof Error) {
			this.#log(error.message, 'error', extra, error.stack);
		} else {
			this.#log(typeof error === 'string' ? error : 'Unknown error', 'error', {
				...(typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {}),
				...extra
			});
		}
	};
}

export default Logger.getInstance();

export type { LogLevel, LoggerOptions };
